import type { Reactive } from 'vue'
import { nanoid } from 'nanoid'

export default defineNuxtPlugin({
  name: 'app:ids',
  enforce: 'pre',
  setup(nuxtApp) {
    const cookies = import.meta.server
      ? useUserSessionCookies()
      : useUserSessionValues()

    const cycle = ref(nanoid())
    const client = ref<string>()

    const idsContext = reactive({
      user: computed(() => cookies.user.value),
      session: computed(() => cookies.session.value),
      cycle,
      client,
    }) satisfies IDsContext

    nuxtApp.provide('ids', idsContext)
  },
})

const USER_ID_STATE_KEY = 'cookie-user-id' as const
const SESSION_ID_STATE_KEY = 'cookie-session-id' as const

export function useUserSessionCookies() {
  const options = {
    secure: true,
    sameSite: 'none' as const,
    path: '/',
    httpOnly: true,
    default: () => nanoid(),
  }

  const userCookie = useCookie('User-ID', {
    ...options,
    expires: monthsAdd(new Date(), 3),
  })
  const sessionCookie = useCookie('Session-ID', options)

  const user = useState(USER_ID_STATE_KEY, () => userCookie.value)
  const session = useState(SESSION_ID_STATE_KEY, () => sessionCookie.value)

  return { user, session }
}

export function useUserSessionValues() {
  const user = useState(USER_ID_STATE_KEY, () => {
    if (import.meta.dev) {
      console.error('User-ID cookie should be defined on the server')
    }

    return nanoid()
  })

  const session = useState(SESSION_ID_STATE_KEY, () => {
    if (import.meta.dev) {
      console.error('Session-ID cookie should be defined on the server')
    }
    return nanoid()
  })

  return { user, session }
}

type IDsContext = Reactive<{
  readonly user: string | null | undefined
  readonly session: string | null | undefined
  cycle: string
  client: string | undefined
}>

declare module '#app' {
  interface NuxtApp {
    $ids: IDsContext
  }
}

declare module '@vue/runtime-core' {
  interface ComponentCustomProperties {
    $ids: IDsContext
  }
}

declare module 'vue' {
  interface ComponentCustomProperties {
    $ids: IDsContext
  }
}

export {}
